import { useEffect } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Car,
  Users,
  FileText,
  Shield,
  Building2,
  LogOut,
  Receipt,
  Banknote,
  CreditCard,
  FileCheck,
  Wrench,
  BarChart3,
  ArrowLeftRight,
  MapPin,
  FileSignature,
  FileCode,
  Clock,
  UserCheck,
  HandCoins,
  Percent,
  PiggyBank,
  ClipboardList,
  Search,
  Calculator,
  DollarSign,
  ClipboardCheck,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import primelaneLogo from '@/assets/primelane-logo.png';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import React from 'react';

const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'> & { title: string; icon?: React.ReactNode }
>(({ className, title, children, icon, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
            className
          )}
          {...props}
        >
          <div className="flex items-center gap-2 text-base font-medium leading-none">
            {icon}
            {title}
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = 'ListItem';

// Section header component for grouped items
const SectionHeader = ({ title }: { title: string }) => (
  <li className="px-3 pt-3 pb-1">
    <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">{title}</span>
  </li>
);

export default function DashboardLayout() {
  const { user, isAuthorized, isLoading, signOut } = useAuth();
  const { hasAccess, canAccessRoute, defaultRoute, isLoading: permsLoading } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && (!user || !isAuthorized)) {
      navigate('/auth');
    }
  }, [user, isAuthorized, isLoading, navigate]);

  // Global permission guard: redirect restricted admins from unauthorized routes
  useEffect(() => {
    if (permsLoading || isLoading) return;
    if (!canAccessRoute(location.pathname)) {
      navigate(defaultRoute, { replace: true });
    }
  }, [location.pathname, permsLoading, isLoading, canAccessRoute, defaultRoute, navigate]);

  if (isLoading || permsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user || !isAuthorized) {
    return null;
  }

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const isActive = (path: string) => location.pathname === path;
  const startsWithPath = (path: string) => location.pathname.startsWith(path);

  // Shorthand for route access checks in nav
  const r = (path: string) => canAccessRoute(path);

  // Group-level visibility — hide entire menu if no children accessible
  const showOverview = r('/dashboard/portfolio') || r('/dashboard/transactions');
  const showFleet = r('/dashboard/vehicles') || r('/dashboard/drivers') || r('/dashboard/driver-applications') || r('/dashboard/trackers');
  const showBusiness = r('/dashboard/contracts') || r('/dashboard/agreements') || r('/dashboard/repayments') || r('/dashboard/loans') || r('/dashboard/expenses') || r('/dashboard/valuations') || r('/dashboard/pricing');
  const showCompliance = r('/dashboard/insurance') || r('/dashboard/roadworthy') || r('/dashboard/maintenance') || r('/dashboard/inspections');
  const showPartners = r('/dashboard/partners') || r('/dashboard/partner-applications');

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="flex h-16 items-center justify-between px-4 lg:px-6">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center">
            <img src={primelaneLogo} alt="Primelane" className="h-8" />
          </Link>

          {/* Navigation Menu */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              {/* Overview */}
              {showOverview && (
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn((isActive('/dashboard') || isActive('/dashboard/portfolio') || isActive('/dashboard/transactions')) && 'bg-accent')}>
                  Overview
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[350px] gap-3 p-4">
                    <ListItem
                      href="/dashboard"
                      title="Analytics"
                      icon={<BarChart3 className="h-4 w-4" />}
                    >
                      Financial performance and trends
                    </ListItem>
                    {r('/dashboard/portfolio') && (
                    <ListItem
                      href="/dashboard/portfolio"
                      title="Portfolio"
                      icon={<LayoutDashboard className="h-4 w-4" />}
                    >
                      All-time fleet overview and status
                    </ListItem>
                    )}
                    {r('/dashboard/transactions') && (
                    <ListItem
                      href="/dashboard/transactions"
                      title="Transactions"
                      icon={<ArrowLeftRight className="h-4 w-4" />}
                    >
                      Centralized financial ledger
                    </ListItem>
                    )}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              )}

              {/* Fleet */}
              {showFleet && (
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn((startsWithPath('/dashboard/vehicles') || isActive('/dashboard/drivers') || isActive('/dashboard/driver-applications') || isActive('/dashboard/trackers')) && 'bg-accent')}>
                  Fleet
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="flex w-max p-4 gap-6">
                    {/* Vehicles Column */}
                    {r('/dashboard/vehicles') && (
                    <>
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="Vehicles" />
                      <ListItem href="/dashboard/vehicles" title="Analytics" icon={<BarChart3 className="h-4 w-4" />}>Fleet performance metrics</ListItem>
                      <ListItem href="/dashboard/vehicles/records" title="All Vehicles" icon={<Car className="h-4 w-4" />}>Manage fleet vehicles</ListItem>
                      <ListItem href="/dashboard/vehicles/procurement" title="Procurement" icon={<ClipboardList className="h-4 w-4" />}>Vehicle procurement pipeline</ListItem>
                      <ListItem href="/dashboard/vehicles/contracts" title="Contracts" icon={<FileText className="h-4 w-4" />}>Vehicle contract stages</ListItem>
                    </ul>
                    <ul className="min-w-[160px] space-y-1">
                      <li className="px-3 pt-3 pb-1" aria-hidden="true"><span className="text-xs invisible">&nbsp;</span></li>
                      <ListItem href="/dashboard/vehicles/transactions" title="Transactions" icon={<ArrowLeftRight className="h-4 w-4" />}>Procurement transactions</ListItem>
                    </ul>
                    </>
                    )}

                    {/* Drivers Column */}
                    {(r('/dashboard/drivers') || r('/dashboard/driver-applications')) && (
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="Drivers" />
                      {r('/dashboard/drivers') && (
                      <ListItem href="/dashboard/drivers" title="All Drivers" icon={<Users className="h-4 w-4" />}>Manage driver profiles</ListItem>
                      )}
                      {r('/dashboard/driver-applications') && (
                      <ListItem href="/dashboard/driver-applications" title="Applications" icon={<ClipboardCheck className="h-4 w-4" />}>Website driver applications</ListItem>
                      )}
                    </ul>
                    )}

                    {/* GPS Trackers Column */}
                    {r('/dashboard/trackers') && (
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="GPS Trackers" />
                      <ListItem href="/dashboard/trackers" title="All Trackers" icon={<MapPin className="h-4 w-4" />}>Manage GPS tracker inventory</ListItem>
                    </ul>
                    )}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
              )}

              {/* Business */}
              {showBusiness && (
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn((startsWithPath('/dashboard/contracts') || isActive('/dashboard/repayments') || startsWithPath('/dashboard/loans') || isActive('/dashboard/expenses') || isActive('/dashboard/valuations') || startsWithPath('/dashboard/pricing')) && 'bg-accent')}>
                  Business
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="flex w-max p-4 gap-6">
                    {/* Contracts Column — templates live on their own first path segment
                        (`/dashboard/agreements/...`), so they are gated separately from the
                        rest of this column rather than riding on contracts access. */}
                    {(r('/dashboard/contracts') || r('/dashboard/agreements')) && (
                    <>
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="Contracts" />
                      {r('/dashboard/contracts') && (
                      <>
                      <ListItem href="/dashboard/contracts" title="All Contracts" icon={<FileText className="h-4 w-4" />}>Manage rental contracts</ListItem>
                      <ListItem href="/dashboard/contracts/agreements" title="Agreements" icon={<FileSignature className="h-4 w-4" />}>Signed contract documents</ListItem>
                      </>
                      )}
                      {r('/dashboard/agreements') && (
                      <ListItem href="/dashboard/agreements/templates" title="Agreement Templates" icon={<FileCode className="h-4 w-4" />}>Versioned document wording</ListItem>
                      )}
                      {r('/dashboard/contracts') && (
                      <>
                      <ListItem href="/dashboard/contracts/deposits" title="Deposits" icon={<PiggyBank className="h-4 w-4" />}>Contract deposit records</ListItem>
                      <ListItem href="/dashboard/contracts/instalments" title="Instalments" icon={<Clock className="h-4 w-4" />}>Payment schedules</ListItem>
                      </>
                      )}
                    </ul>
                    <ul className="min-w-[160px] space-y-1">
                      <li className="px-3 pt-3 pb-1" aria-hidden="true"><span className="text-xs invisible">&nbsp;</span></li>
                      {r('/dashboard/contracts') && (
                      <ListItem href="/dashboard/contracts/witnesses" title="Witnesses" icon={<UserCheck className="h-4 w-4" />}>Contract witnesses</ListItem>
                      )}
                      {r('/dashboard/repayments') && (
                      <ListItem href="/dashboard/repayments" title="Repayments" icon={<Receipt className="h-4 w-4" />}>Contract repayments</ListItem>
                      )}
                    </ul>
                    </>
                    )}

                    {/* Loans Column */}
                    {r('/dashboard/loans') && (
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="Advances" />
                      <ListItem href="/dashboard/loans" title="All Advances" icon={<Banknote className="h-4 w-4" />}>Manage driver advances</ListItem>
                      <ListItem href="/dashboard/loans/agreements" title="Advance Agreements" icon={<FileSignature className="h-4 w-4" />}>Signed advance documents</ListItem>
                      <ListItem href="/dashboard/loans/repayments" title="Advance Repayments" icon={<HandCoins className="h-4 w-4" />}>Advance payments received</ListItem>
                      <ListItem href="/dashboard/loans/instalments" title="Advance Instalments" icon={<Clock className="h-4 w-4" />}>Advance payment schedules</ListItem>
                    </ul>
                    )}

                    {/* Pricing Column */}
                    {(r('/dashboard/valuations') || r('/dashboard/pricing')) && (
                    <ul className="min-w-[150px] space-y-1">
                      <SectionHeader title="Pricing" />
                      {r('/dashboard/valuations') && (
                      <ListItem href="/dashboard/valuations" title="Valuations" icon={<Calculator className="h-4 w-4" />}>Vehicle cost breakdown</ListItem>
                      )}
                      {r('/dashboard/pricing') && (
                      <>
                      <ListItem href="/dashboard/pricing" title="Vehicle Pricing" icon={<DollarSign className="h-4 w-4" />}>Financial model &amp; returns</ListItem>
                      <ListItem href="/dashboard/pricing/renewals" title="Insurance Renewals" icon={<Shield className="h-4 w-4" />}>Renewal financing simulator</ListItem>
                      </>
                      )}
                    </ul>
                    )}

                    {/* Expenses Column */}
                    {r('/dashboard/expenses') && (
                    <ul className="min-w-[150px] space-y-1">
                      <SectionHeader title="Expenses" />
                      <ListItem href="/dashboard/expenses" title="All Expenses" icon={<CreditCard className="h-4 w-4" />}>Manage business expenses</ListItem>
                    </ul>
                    )}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
              )}

              {/* Compliance */}
              {showCompliance && (
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn((startsWithPath('/dashboard/insurance') || isActive('/dashboard/roadworthy') || isActive('/dashboard/maintenance') || startsWithPath('/dashboard/inspections')) && 'bg-accent')}>
                  Compliance
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="flex w-max p-4 gap-6">
                    {/* Insurance Column */}
                    {r('/dashboard/insurance') && (
                    <>
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="Insurance" />
                      <ListItem href="/dashboard/insurance" title="All Insurance" icon={<Shield className="h-4 w-4" />}>Manage insurance policies</ListItem>
                      <ListItem href="/dashboard/insurance/premiums" title="Premiums" icon={<Receipt className="h-4 w-4" />}>Third-party premium payments</ListItem>
                      <ListItem href="/dashboard/insurance/claims" title="Claims" icon={<ClipboardList className="h-4 w-4" />}>Insurance claims</ListItem>
                      {/* "Premium Years" rather than "Insurance Premiums" [D-B46 R9]: the entry two
                          rows up is the insurer-side outflow and is already called "Premiums", so
                          the two would collide in one menu column. */}
                      <ListItem href="/dashboard/insurance/loans" title="Premium Years" icon={<Banknote className="h-4 w-4" />}>Advanced or paid in full, by policy year</ListItem>
                    </ul>
                    <ul className="min-w-[160px] space-y-1">
                      <li className="px-3 pt-3 pb-1" aria-hidden="true"><span className="text-xs invisible">&nbsp;</span></li>
                      <ListItem href="/dashboard/insurance/discounts" title="Discounts" icon={<Percent className="h-4 w-4" />}>Applied insurance discounts</ListItem>
                      <ListItem href="/dashboard/insurance/deposits" title="Insurance Deposits" icon={<PiggyBank className="h-4 w-4" />}>What drivers paid toward their premiums</ListItem>
                    </ul>
                    </>
                    )}

                    {/* Vehicle Compliance Section */}
                    {(r('/dashboard/roadworthy') || r('/dashboard/maintenance') || r('/dashboard/inspections')) && (
                    <ul className="min-w-[160px] space-y-1">
                      <SectionHeader title="Vehicle Compliance" />
                      {r('/dashboard/roadworthy') && (
                      <ListItem href="/dashboard/roadworthy" title="Roadworthiness" icon={<FileCheck className="h-4 w-4" />}>Track roadworthy certificates</ListItem>
                      )}
                      {r('/dashboard/maintenance') && (
                      <ListItem href="/dashboard/maintenance" title="Maintenance" icon={<Wrench className="h-4 w-4" />}>Vehicle maintenance records</ListItem>
                      )}
                      {r('/dashboard/inspections') && (
                      <ListItem href="/dashboard/inspections" title="Inspections" icon={<Search className="h-4 w-4" />}>Vehicle inspections & photos</ListItem>
                      )}
                    </ul>
                    )}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
              )}

              {/* Partners */}
              {showPartners && (
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn((isActive('/dashboard/partners') || isActive('/dashboard/partners/assignments') || isActive('/dashboard/partner-applications')) && 'bg-accent')}>
                  Partners
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[350px] gap-3 p-4">
                    {r('/dashboard/partners') && (
                    <>
                    <ListItem href="/dashboard/partners" title="All Partners" icon={<Building2 className="h-4 w-4" />}>Partners (individuals or organizations)</ListItem>
                    <ListItem href="/dashboard/partners/assignments" title="Payouts" icon={<ClipboardCheck className="h-4 w-4" />}>Assign transactions to weekly payouts</ListItem>
                    </>
                    )}
                    {r('/dashboard/partner-applications') && (
                    <ListItem href="/dashboard/partner-applications" title="Applications" icon={<ClipboardCheck className="h-4 w-4" />}>Website partner applications</ListItem>
                    )}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              )}
            </NavigationMenuList>
          </NavigationMenu>

          {/* User & Sign Out */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2 text-sm font-normal text-muted-foreground hover:text-foreground">
                  <span className="hidden sm:inline">{user.email || user.phone || 'Account'}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                {hasAccess('settings') && (
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} className="cursor-pointer">
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="flex gap-1 overflow-x-auto border-t border-border p-2 md:hidden">
          {r('/dashboard') && <Button variant={isActive('/dashboard') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard')}><BarChart3 className="h-4 w-4" /></Button>}
          {r('/dashboard/transactions') && <Button variant={isActive('/dashboard/transactions') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/transactions')}><ArrowLeftRight className="h-4 w-4" /></Button>}
          {r('/dashboard/vehicles') && <Button variant={isActive('/dashboard/vehicles') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/vehicles')}><Car className="h-4 w-4" /></Button>}
          {r('/dashboard/drivers') && <Button variant={isActive('/dashboard/drivers') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/drivers')}><Users className="h-4 w-4" /></Button>}
          {r('/dashboard/contracts') && <Button variant={isActive('/dashboard/contracts') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/contracts')}><FileText className="h-4 w-4" /></Button>}
          {r('/dashboard/loans') && <Button variant={isActive('/dashboard/loans') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/loans')}><Banknote className="h-4 w-4" /></Button>}
          {r('/dashboard/insurance') && <Button variant={isActive('/dashboard/insurance') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/insurance')}><Shield className="h-4 w-4" /></Button>}
          {r('/dashboard/maintenance') && <Button variant={isActive('/dashboard/maintenance') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/maintenance')}><Wrench className="h-4 w-4" /></Button>}
          {r('/dashboard/inspections') && <Button variant={startsWithPath('/dashboard/inspections') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/inspections')} aria-label="Inspections"><ClipboardCheck className="h-4 w-4" /></Button>}
          {r('/dashboard/partners') && <Button variant={isActive('/dashboard/partners') ? 'secondary' : 'ghost'} size="sm" onClick={() => navigate('/dashboard/partners')}><Building2 className="h-4 w-4" /></Button>}
        </div>
      </header>

      {/* Page content */}
      <main className="p-4 lg:p-6">
        <Outlet />
      </main>
    </div>
  );
}
